import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Loader2, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { formatPrice, priceListFor, DEFAULT_CURRENCY } from '@/config/pricing';
import CheckoutDialog from '@/components/checkout/CheckoutDialog';

// ─── Extra credits ────────────────────────────────────────────────────────────
// Packs of message credits on top of a plan, for Pro and Studio. Bought
// credits never expire at the monthly reset (services/credits.js), which is
// said here because it is the question everyone asks before buying.
//
// Packs and prices come from GET /api/billing/credit-packs, in the buyer's own
// currency; the bundled table only fills the first paint.

const API_URL = import.meta.env.VITE_API_URL ?? '';

/** Price per credit, for the "best value" comparison. */
function perCredit(pack) {
  return pack.price / pack.credits;
}

/**
 * @param {object} props
 * @param {boolean} [props.compact]        drop the heading, for tight spaces
 * @param {() => void} [props.onPurchased] called when the checkout closes
 */
export default function CreditPacks({ compact = false, onPurchased }) {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(() => {
    const list = priceListFor(DEFAULT_CURRENCY);
    return { currency: list.currency, symbol: list.symbol, locale: list.locale, packs: list.creditPacks, canBuy: false, tier: null, purchasedCredits: 0, loaded: false };
  });
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/billing/credit-packs`, {
      headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d?.packs) setData({ ...d, loaded: true }); })
      .catch(() => { if (!cancelled) setData(cur => ({ ...cur, loaded: true })); });
    return () => { cancelled = true; };
  }, [session?.access_token]);

  const pricing = { currency: data.currency, symbol: data.symbol, locale: data.locale };
  const best = data.packs.reduce((b, p) => (!b || perCredit(p) < perCredit(b) ? p : b), null);
  const locked = !data.canBuy;

  const buy = (pack) => {
    if (!user) { navigate('/Pricing'); return; }
    if (locked) { navigate('/Pricing'); return; }
    setCheckout({
      purpose: 'credits',
      packId: pack.id,
      title: `${pack.credits} extra credits`,
      subtitle: 'One-off · never expire',
      price: pack.price,
      pricing,
    });
  };

  return (
    <div>
      {!compact && (
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2"> Extra credits</h3>
            <p className="text-zinc-500 text-sm mt-1">
              Top up whenever you run low. Bought credits are kept when your plan renews — they never expire.
            </p>
          </div>
          {data.purchasedCredits > 0 && (
            <span className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1">
              {data.purchasedCredits} bought credits in your balance
            </span>
          )}
        </div>
      )}

      {locked && data.loaded && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 px-4 py-3">
          <Lock className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-400 leading-relaxed">
            Extra credits are available on the <span className="text-white font-medium">Pro</span> and{' '}
            <span className="text-white font-medium">Studio</span> plans.{' '}
            <button onClick={() => navigate('/Pricing')} className="text-orange-400 hover:text-orange-300 font-medium">See plans</button>
          </p>
        </div>
      )}

      <div className={`grid gap-3 ${compact ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'}`}>
        {data.packs.map(pack => (
          <div
            key={pack.id}
            className={`relative rounded-2xl border p-4 flex flex-col gap-3 ${
              best?.id === pack.id ? 'border-orange-500/50 bg-orange-500/[0.06]' : 'border-zinc-800 bg-zinc-900/70'
            }`}
          >
            {best?.id === pack.id && data.packs.length > 1 && (
              <span className="absolute -top-2.5 left-4 px-2 py-0.5 bg-orange-500 text-ink text-[10px] font-bold rounded-full uppercase tracking-wider">Best value</span>
            )}
            <div>
              <p className="text-2xl font-extrabold text-white tabular-nums">{pack.credits}</p>
              <p className="text-xs text-zinc-500">credits</p>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-lg font-bold text-white">{formatPrice(pack.price, pricing)}</span>
              <span className="text-[11px] text-zinc-500">{formatPrice(Math.round(perCredit(pack) * 100) / 100, pricing)}/credit</span>
            </div>
            <button
              onClick={() => buy(pack)}
              disabled={!data.loaded}
              aria-label={`Buy ${pack.credits} credits`}
              className={`h-9 rounded-full text-sm font-semibold transition-colors disabled:opacity-50 ${
                locked
                  ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700'
                  : 'bg-orange-500 hover:bg-orange-400 text-ink'
              }`}
            >
              {!data.loaded ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : locked ? 'Upgrade to buy' : 'Buy credits'}
            </button>
          </div>
        ))}
      </div>

      {!locked && (
        <p className="mt-3 text-[11px] text-zinc-600 flex items-center gap-1.5">
          <Check className="w-3 h-3" /> Added the moment your payment clears — card, bank, mobile money or crypto.
        </p>
      )}

      {checkout && (
        <CheckoutDialog
          purchase={checkout}
          onClose={() => { setCheckout(null); onPurchased?.(); }}
        />
      )}
    </div>
  );
}
